import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtError } from "@/lib/api";
import { Card } from "@/components/ui-kit";
import { Loader, EmptyState } from "@/components/Loader";
import { ArrowLeft, Plus, Trash2, X, Users, Loader2, Star } from "lucide-react";
import { toast } from "sonner";

export default function TrustedContactsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", relationship: "Family", phone: "", email: "", is_primary: false });

  const load = () => api.get("/trusted-contacts").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name || !form.phone) { toast.error("Name and phone are required"); return; }
    setBusy(true);
    try {
      await api.post("/trusted-contacts", form);
      toast.success("Trusted contact added");
      setAdding(false);
      setForm({ name: "", relationship: "Family", phone: "", email: "", is_primary: false });
      load();
    } catch (e) { toast.error(fmtError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/trusted-contacts/${id}`); toast.success("Contact removed"); load(); }
    catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
  };

  if (items === null) return <Loader />;

  return (
    <div className="ll-fade-up" data-testid="trusted-contacts-screen">
      <div className="px-5 pt-8 pb-2 flex items-center gap-3">
        <button onClick={() => navigate("/profile")} data-testid="tc-back" className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm"><ArrowLeft className="w-5 h-5" /></button>
        <h1 className="text-2xl font-bold font-head tracking-tight">Trusted contacts</h1>
      </div>
      <div className="px-5 mt-3">
        <button data-testid="tc-add" onClick={() => setAdding(true)} className="w-full bg-[#F97316] text-white rounded-2xl py-4 font-bold flex items-center justify-center gap-2 mb-5 min-h-[56px]"><Plus className="w-5 h-5" /> Add contact</button>
        {items.length === 0 ? (
          <EmptyState icon={Users} title="No trusted contacts" subtitle="Add at least two people who should be alerted in an emergency." />
        ) : (
          <div className="space-y-3">
            {items.map((c) => (
              <Card key={c.id} className="flex items-center gap-3 p-4" data-testid={`tc-item-${c.id}`}>
                <div className="w-12 h-12 rounded-2xl bg-[#C4B5FD]/30 flex items-center justify-center font-bold">{c.name[0]}</div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold flex items-center gap-1.5">{c.name} {c.is_primary && <Star className="w-4 h-4 text-[#F97316] fill-[#F97316]" />}</p>
                  <p className="text-sm text-[#111827]/50 truncate">{c.relationship} · {c.phone}</p>
                </div>
                <button data-testid={`tc-delete-${c.id}`} onClick={() => remove(c.id)} className="w-10 h-10 rounded-full bg-[#DC2626]/10 text-[#DC2626] flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
              </Card>
            ))}
          </div>
        )}
      </div>

      {adding && (
        <div className="fixed inset-0 bg-[#111827]/50 z-[60] flex items-end sm:items-center justify-center" onClick={() => setAdding(false)}>
          <div className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6 ll-fade-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4"><h2 className="text-2xl font-bold font-head">Add contact</h2><button data-testid="tc-close" onClick={() => setAdding(false)} className="w-9 h-9 rounded-full bg-[#111827]/5 flex items-center justify-center"><X className="w-5 h-5" /></button></div>
            <div className="space-y-3">
              <input data-testid="tc-name" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#FFF7E6] border-2 border-[#111827]/10 rounded-xl p-4 w-full outline-none focus:border-[#F97316]" />
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="tc-relationship" placeholder="Relationship" value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })} className="bg-[#FFF7E6] border-2 border-[#111827]/10 rounded-xl p-4 outline-none focus:border-[#F97316]" />
                <input data-testid="tc-phone" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#FFF7E6] border-2 border-[#111827]/10 rounded-xl p-4 outline-none focus:border-[#F97316]" />
              </div>
              <input data-testid="tc-email" placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#FFF7E6] border-2 border-[#111827]/10 rounded-xl p-4 w-full outline-none focus:border-[#F97316]" />
              <label className="flex items-center gap-2 text-[#111827]/70"><input type="checkbox" data-testid="tc-primary" checked={form.is_primary} onChange={(e) => setForm({ ...form, is_primary: e.target.checked })} className="w-5 h-5 accent-[#F97316]" /> Set as primary contact</label>
              <button data-testid="tc-save" onClick={save} disabled={busy} className="w-full bg-[#F97316] text-white rounded-2xl py-4 font-bold min-h-[56px] flex items-center justify-center">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Save contact"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
